import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Chip, Alert, CircularProgress,
  Grid, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab,
  Tooltip, Divider, FormControlLabel, Switch, Collapse
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Refresh as RefreshIcon, Search as SearchIcon, ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon, AttachFile as AttachFileIcon,
  Close as CloseIcon, Download as DownloadIcon
} from '@mui/icons-material';
import { API_BASE_URL } from '../config/config';

const INCIDENT_TYPES = ['System Outage', 'Data Issue', 'Security', 'Hardware', 'Network', 'Software', 'Power', 'Other'];
const SEVERITIES = ['Critical', 'High', 'Medium', 'Low'];
const STATUSES = ['Open', 'In Progress', 'Resolved', 'Closed'];
const FIX_TYPES = ['', 'Config Change', 'Code Fix', 'Restart', 'Hardware Replacement', 'Rollback', 'Vendor Fix', 'Other'];
const LOCATIONS = [
  'FIDS Screen – Terminal A', 'FIDS Screen – Terminal B', 'FIDS Screen – Arrivals Hall',
  'Backend API', 'Frontend Web', 'Database', 'Network Infrastructure', 'Kiosk Device',
  'SITA Integration', 'Nginx / Proxy', 'Other'
];

const SEVERITY_COLORS = {
  Critical: 'error',
  High: 'warning',
  Medium: 'info',
  Low: 'default'
};

const STATUS_COLORS = {
  Open: 'error',
  'In Progress': 'warning',
  Resolved: 'success',
  Closed: 'default'
};

const emptyIncident = {
  title: '', dateTimeOfIncident: '', dateTimeReported: '', reportedBy: '',
  locationOrSystem: '', incidentType: '', category: '', subcategory: '',
  severity: '', impactLevel: '', description: '', symptoms: '',
  expectedBehavior: '', actualBehavior: '', stepsToReproduce: '',
  logMessages: '', relatedTickets: '', immediateActions: '',
  workaroundApplied: false, workaroundDetails: '', assignedTo: '',
  status: 'Open', timeDetected: '', timeAcknowledged: '', timeResolved: '',
  rootCause: '', resolutionSummary: '', fixType: '', wasPreventable: ''
};

function formatDateTime(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString();
}

function toInputDateTime(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

const IncidentReporting = () => {
  const [incidents, setIncidents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterType, setFilterType] = useState('');
  const [searchText, setSearchText] = useState('');

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // create | edit | view
  const [form, setForm] = useState({ ...emptyIncident });
  const [formFiles, setFormFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [formTab, setFormTab] = useState(0);

  // Detail expand
  const [expandedId, setExpandedId] = useState(null);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);

  const getAuthData = () => {
    try { return JSON.parse(localStorage.getItem('auth') || '{}'); }
    catch { return {}; }
  };

  const getAuthToken = () => getAuthData().token || '';
  const getUserEmail = () => getAuthData().data?.email || 'Unknown';
  const getUserRole = () => getAuthData().data?.role || 'viewer';
  const isAdmin = getUserRole() === 'admin';

  const fetchIncidents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterSeverity) params.append('severity', filterSeverity);
      if (filterType) params.append('incidentType', filterType);
      if (searchText) params.append('search', searchText);
      params.append('limit', '100');

      const res = await fetch(`${API_BASE_URL}/api/admin/incidents?${params}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      const json = await res.json();
      if (json.success) {
        setIncidents(json.data);
        setTotal(json.total);
      } else {
        setError(json.message || 'Failed to load incidents');
      }
    } catch (err) {
      setError('Failed to load incidents: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterSeverity, filterType, searchText]);

  useEffect(() => { fetchIncidents(); }, [fetchIncidents]);

  const openCreate = () => {
    const now = toInputDateTime(new Date());
    setForm({ ...emptyIncident, dateTimeReported: now, reportedBy: getUserEmail() });
    setFormFiles([]);
    setFormError('');
    setFormTab(0);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const openEdit = (incident) => {
    setForm({
      ...emptyIncident,
      ...incident,
      dateTimeOfIncident: toInputDateTime(incident.dateTimeOfIncident),
      dateTimeReported: toInputDateTime(incident.dateTimeReported),
      timeDetected: toInputDateTime(incident.timeDetected),
      timeAcknowledged: toInputDateTime(incident.timeAcknowledged),
      timeResolved: toInputDateTime(incident.timeResolved),
    });
    setFormFiles([]);
    setFormError('');
    setFormTab(0);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const openView = (incident) => {
    setForm({
      ...emptyIncident,
      ...incident,
      dateTimeOfIncident: toInputDateTime(incident.dateTimeOfIncident),
      dateTimeReported: toInputDateTime(incident.dateTimeReported),
      timeDetected: toInputDateTime(incident.timeDetected),
      timeAcknowledged: toInputDateTime(incident.timeAcknowledged),
      timeResolved: toInputDateTime(incident.timeResolved),
    });
    setFormTab(0);
    setDialogMode('view');
    setDialogOpen(true);
  };

  const handleFormChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    // Client-side validation for required fields
    const missing = [];
    if (!form.title?.trim()) missing.push('Title');
    if (!form.dateTimeOfIncident) missing.push('Date/Time of Incident');
    if (!form.reportedBy?.trim()) missing.push('Reported By');
    if (!form.locationOrSystem?.trim()) missing.push('Location / System');
    if (!form.incidentType) missing.push('Incident Type');
    if (!form.severity) missing.push('Severity');
    if (!form.description?.trim()) missing.push('Description');
    if (missing.length) {
      setFormError(`Please fill in required fields: ${missing.join(', ')}`);
      return;
    }

    setSaving(true);
    setFormError('');

    try {
      const isEdit = dialogMode === 'edit';
      const url = isEdit
        ? `${API_BASE_URL}/api/admin/incidents/${form._id}`
        : `${API_BASE_URL}/api/admin/incidents`;

      const formData = new FormData();

      // Append all non-empty fields — skip empty strings so Mongoose
      // enum/date fields don't receive '' and fail validation
      Object.entries(form).forEach(([key, val]) => {
        if (key === '_id' || key === '__v' || key === 'attachments' || key === 'createdAt' || key === 'updatedAt') return;
        if (val !== null && val !== undefined && val !== '') {
          formData.append(key, val);
        }
      });

      // Append files
      for (const file of formFiles) {
        formData.append('attachments', file);
      }

      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        body: formData
      });

      const json = await res.json();
      if (json.success) {
        setSuccess(isEdit ? 'Incident updated successfully' : 'Incident created successfully');
        setDialogOpen(false);
        fetchIncidents();
      } else {
        setFormError(json.message || 'Failed to save incident');
      }
    } catch (err) {
      setFormError('Error saving: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/incidents/${deleteTarget._id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      const json = await res.json();
      if (json.success) {
        setSuccess('Incident deleted');
        fetchIncidents();
      } else {
        setError(json.message || 'Failed to delete');
      }
    } catch (err) {
      setError('Error deleting: ' + err.message);
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleRemoveAttachment = async (incidentId, filename) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/incidents/${incidentId}/attachments/${filename}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      const json = await res.json();
      if (json.success) {
        setForm(prev => ({
          ...prev,
          attachments: prev.attachments.filter(a => a.filename !== filename)
        }));
      }
    } catch (err) {
      setFormError('Failed to remove attachment');
    }
  };

  const readOnly = dialogMode === 'view';
  // For non-admin (ICT/ops): tabs 5-7 (Actions, Timeline, Resolution) are read-only
  const adminOnlyReadOnly = readOnly || !isAdmin;

  // ─── Form Sections ───────────────────────────────────────────────
  const renderBasicInfo = () => (
    <Grid container spacing={2}>
      {dialogMode === 'edit' && (
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Incident ID" value={form.incidentId || ''} disabled size="small" />
        </Grid>
      )}
      <Grid item xs={12} sm={dialogMode === 'edit' ? 6 : 12}>
        <TextField fullWidth required label="Title / Short Summary" value={form.title}
          onChange={handleFormChange('title')} disabled={readOnly} size="small" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth required type="datetime-local" label="Date & Time of Incident"
          value={form.dateTimeOfIncident} onChange={handleFormChange('dateTimeOfIncident')}
          InputLabelProps={{ shrink: true }} disabled={readOnly} size="small" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth type="datetime-local" label="Date & Time Reported"
          value={form.dateTimeReported} onChange={handleFormChange('dateTimeReported')}
          InputLabelProps={{ shrink: true }} disabled={readOnly} size="small" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth required label="Reported By" value={form.reportedBy}
          onChange={handleFormChange('reportedBy')} disabled={readOnly} size="small" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth required select label="Location / System Affected" value={form.locationOrSystem}
          onChange={handleFormChange('locationOrSystem')} disabled={readOnly} size="small">
          {LOCATIONS.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
        </TextField>
      </Grid>
    </Grid>
  );

  const renderClassification = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth required select label="Incident Type" value={form.incidentType}
          onChange={handleFormChange('incidentType')} disabled={readOnly} size="small">
          {INCIDENT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth required select label="Severity / Priority" value={form.severity}
          onChange={handleFormChange('severity')} disabled={readOnly} size="small">
          {SEVERITIES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="Category" value={form.category}
          onChange={handleFormChange('category')} disabled={readOnly} size="small"
          placeholder="e.g. Network" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="Subcategory" value={form.subcategory}
          onChange={handleFormChange('subcategory')} disabled={readOnly} size="small"
          placeholder="e.g. Connectivity" />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth label="Impact Level" value={form.impactLevel}
          onChange={handleFormChange('impactLevel')} disabled={readOnly} size="small"
          placeholder="e.g. All departure boards down, 500+ passengers affected" />
      </Grid>
    </Grid>
  );

  const renderDescription = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField fullWidth required multiline rows={3} label="Detailed Description"
          value={form.description} onChange={handleFormChange('description')} disabled={readOnly} size="small" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth multiline rows={2} label="Symptoms (What Happened)"
          value={form.symptoms} onChange={handleFormChange('symptoms')} disabled={readOnly} size="small" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth multiline rows={2} label="Expected vs Actual Behavior"
          value={form.expectedBehavior} onChange={handleFormChange('expectedBehavior')} disabled={readOnly} size="small" />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth multiline rows={2} label="Steps to Reproduce"
          value={form.stepsToReproduce} onChange={handleFormChange('stepsToReproduce')} disabled={readOnly} size="small" />
      </Grid>
    </Grid>
  );

  const renderEvidence = () => (
    <Grid container spacing={2}>
      {!readOnly && (
        <Grid item xs={12}>
          <Button variant="outlined" component="label" startIcon={<AttachFileIcon />} size="small">
            Add Files (max 5, 10MB each)
            <input type="file" hidden multiple accept="image/*,video/*,.pdf,.txt,.log"
              onChange={(e) => setFormFiles(prev => [...prev, ...Array.from(e.target.files)])} />
          </Button>
          {formFiles.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {formFiles.map((f, i) => (
                <Chip key={i} label={f.name} size="small" onDelete={() => setFormFiles(prev => prev.filter((_, j) => j !== i))} />
              ))}
            </Box>
          )}
        </Grid>
      )}
      {form.attachments?.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Existing Attachments:</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {form.attachments.map((att, i) => (
              <Chip key={i} label={att.originalName} size="small" icon={<AttachFileIcon />}
                onDelete={!readOnly ? () => handleRemoveAttachment(form._id, att.filename) : undefined}
                onClick={() => window.open(`${API_BASE_URL}/uploads/incidents/${att.filename}`, '_blank')}
                clickable />
            ))}
          </Box>
        </Grid>
      )}
      <Grid item xs={12}>
        <TextField fullWidth multiline rows={3} label="Logs / Error Messages"
          value={form.logMessages} onChange={handleFormChange('logMessages')} disabled={readOnly} size="small"
          placeholder="Paste relevant log entries or error messages" />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth label="Related Tickets / Alerts"
          value={form.relatedTickets} onChange={handleFormChange('relatedTickets')} disabled={readOnly} size="small"
          placeholder="e.g. JIRA-123, PagerDuty alert #456" />
      </Grid>
    </Grid>
  );

  const renderActions = () => (
    <Grid container spacing={2}>
      {adminOnlyReadOnly && dialogMode !== 'view' && (
        <Grid item xs={12}>
          <Alert severity="info" variant="outlined">This section is managed by administrators only.</Alert>
        </Grid>
      )}
      <Grid item xs={12}>
        <TextField fullWidth multiline rows={2} label="Immediate Actions Taken"
          value={form.immediateActions} onChange={handleFormChange('immediateActions')} disabled={adminOnlyReadOnly} size="small" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControlLabel
          control={<Switch checked={form.workaroundApplied || false}
            onChange={(e) => setForm(prev => ({ ...prev, workaroundApplied: e.target.checked }))}
            disabled={adminOnlyReadOnly} />}
          label="Workaround Applied?" />
      </Grid>
      {form.workaroundApplied && (
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Workaround Details" value={form.workaroundDetails}
            onChange={handleFormChange('workaroundDetails')} disabled={adminOnlyReadOnly} size="small" />
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="Assigned To" value={form.assignedTo}
          onChange={handleFormChange('assignedTo')} disabled={adminOnlyReadOnly} size="small" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth select label="Status" value={form.status}
          onChange={handleFormChange('status')} disabled={adminOnlyReadOnly} size="small">
          {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
      </Grid>
    </Grid>
  );

  const renderTimeline = () => (
    <Grid container spacing={2}>
      {adminOnlyReadOnly && dialogMode !== 'view' && (
        <Grid item xs={12}>
          <Alert severity="info" variant="outlined">This section is managed by administrators only.</Alert>
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <TextField fullWidth type="datetime-local" label="Time Detected"
          value={form.timeDetected} onChange={handleFormChange('timeDetected')}
          InputLabelProps={{ shrink: true }} disabled={adminOnlyReadOnly} size="small" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth type="datetime-local" label="Time Acknowledged"
          value={form.timeAcknowledged} onChange={handleFormChange('timeAcknowledged')}
          InputLabelProps={{ shrink: true }} disabled={adminOnlyReadOnly} size="small" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth type="datetime-local" label="Time Resolved"
          value={form.timeResolved} onChange={handleFormChange('timeResolved')}
          InputLabelProps={{ shrink: true }} disabled={adminOnlyReadOnly} size="small" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="Downtime Duration" size="small" disabled
          value={form.downtimeDurationMinutes ? `${form.downtimeDurationMinutes} minutes` :
            (form.timeDetected && form.timeResolved ?
              `${Math.round((new Date(form.timeResolved) - new Date(form.timeDetected)) / 60000)} minutes (calculated)` : '—')} />
      </Grid>
    </Grid>
  );

  const renderResolution = () => (
    <Grid container spacing={2}>
      {adminOnlyReadOnly && dialogMode !== 'view' && (
        <Grid item xs={12}>
          <Alert severity="info" variant="outlined">This section is managed by administrators only.</Alert>
        </Grid>
      )}
      <Grid item xs={12}>
        <TextField fullWidth multiline rows={2} label="Root Cause"
          value={form.rootCause} onChange={handleFormChange('rootCause')} disabled={adminOnlyReadOnly} size="small" />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth multiline rows={2} label="Resolution Summary"
          value={form.resolutionSummary} onChange={handleFormChange('resolutionSummary')} disabled={adminOnlyReadOnly} size="small" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth select label="Fix Type" value={form.fixType}
          onChange={handleFormChange('fixType')} disabled={adminOnlyReadOnly} size="small">
          {FIX_TYPES.map(f => <MenuItem key={f} value={f}>{f || '(None)'}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth select label="Was this Preventable?" value={form.wasPreventable}
          onChange={handleFormChange('wasPreventable')} disabled={adminOnlyReadOnly} size="small">
          <MenuItem value="">Unknown</MenuItem>
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
      </Grid>
    </Grid>
  );

  // ICT/ops users: only see tabs 1-4; admins see all 7
  const allFormSections = [
    { label: 'Basic Info', render: renderBasicInfo, adminOnly: false },
    { label: 'Classification', render: renderClassification, adminOnly: false },
    { label: 'Description', render: renderDescription, adminOnly: false },
    { label: 'Evidence', render: renderEvidence, adminOnly: false },
    { label: 'Actions', render: renderActions, adminOnly: true },
    { label: 'Timeline', render: renderTimeline, adminOnly: true },
    { label: 'Resolution', render: renderResolution, adminOnly: true },
  ];

  const formSections = isAdmin ? allFormSections : allFormSections.filter(s => !s.adminOnly);

  // ─── Main Render ─────────────────────────────────────────────────
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Incident Reporting</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} size="small">
            New Incident
          </Button>
          <IconButton onClick={fetchIncidents} size="small"><RefreshIcon /></IconButton>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label="Search" value={searchText}
              onChange={e => setSearchText(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchIncidents()}
              InputProps={{ endAdornment: <IconButton size="small" onClick={fetchIncidents}><SearchIcon /></IconButton> }} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth size="small" select label="Status" value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth size="small" select label="Severity" value={filterSeverity}
              onChange={e => setFilterSeverity(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {SEVERITIES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth size="small" select label="Type" value={filterType}
              onChange={e => setFilterType(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {INCIDENT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="body2" color="text.secondary">
              {total} incident{total !== 1 ? 's' : ''} found
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Incidents Table */}
      <TableContainer component={Paper} sx={{ backgroundColor: '#000' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 'bold', backgroundColor: '#2a2a2a', color: '#fff' } }}>
              <TableCell width={40} sx={{ color: '#fff' }} />
              <TableCell sx={{ color: '#fff' }}>ID</TableCell>
              <TableCell sx={{ color: '#fff' }}>Title</TableCell>
              <TableCell sx={{ color: '#fff' }}>Type</TableCell>
              <TableCell sx={{ color: '#fff' }}>Severity</TableCell>
              <TableCell sx={{ color: '#fff' }}>Status</TableCell>
              <TableCell sx={{ color: '#fff' }}>Location</TableCell>
              <TableCell sx={{ color: '#fff' }}>Reported</TableCell>
              <TableCell width={100} sx={{ color: '#fff' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#fff', backgroundColor: '#000' }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : incidents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#fff', backgroundColor: '#000' }}>
                  <Typography sx={{ color: '#fff' }}>No incidents found</Typography>
                </TableCell>
              </TableRow>
            ) : incidents.map((inc, idx) => (
              <React.Fragment key={inc._id}>
                <TableRow hover sx={{ cursor: 'pointer', backgroundColor: idx % 2 === 0 ? '#1a1a1a' : '#000', '&:hover': { backgroundColor: '#2a2a2a' } }} onClick={() => openView(inc)}>
                  <TableCell sx={{ color: '#fff' }}>
                    <IconButton size="small" sx={{ color: '#fff' }} onClick={(e) => { e.stopPropagation(); setExpandedId(expandedId === inc._id ? null : inc._id); }}>
                      {expandedId === inc._id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ color: '#fff', fontFamily: 'monospace', fontSize: '0.8rem' }}>{inc.incidentId}</TableCell>
                  <TableCell sx={{ color: '#fff', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {inc.title}
                  </TableCell>
                  <TableCell sx={{ color: '#fff' }}>{inc.incidentType}</TableCell>
                  <TableCell><Chip label={inc.severity} size="small" color={SEVERITY_COLORS[inc.severity] || 'default'} /></TableCell>
                  <TableCell><Chip label={inc.status} size="small" color={STATUS_COLORS[inc.status] || 'default'} variant="outlined" sx={{ color: '#fff', borderColor: '#fff' }} /></TableCell>
                  <TableCell sx={{ color: '#fff', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {inc.locationOrSystem}
                  </TableCell>
                  <TableCell sx={{ color: '#fff', fontSize: '0.8rem' }}>{formatDateTime(inc.dateTimeReported)}</TableCell>
                  <TableCell sx={{ color: '#fff' }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" sx={{ color: '#fff' }} onClick={(e) => { e.stopPropagation(); openEdit(inc); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {isAdmin && (
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setDeleteTarget(inc); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={9} sx={{ p: 0, borderBottom: expandedId === inc._id ? undefined : 'none', backgroundColor: '#111' }}>
                    <Collapse in={expandedId === inc._id}>
                      <Box sx={{ p: 2, backgroundColor: '#111' }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" sx={{ color: '#aaa' }}>Description</Typography>
                            <Typography variant="body2" sx={{ color: '#fff' }}>{inc.description || '—'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="caption" sx={{ color: '#aaa' }}>Assigned To</Typography>
                            <Typography variant="body2" sx={{ color: '#fff' }}>{inc.assignedTo || '—'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="caption" sx={{ color: '#aaa' }}>Impact</Typography>
                            <Typography variant="body2" sx={{ color: '#fff' }}>{inc.impactLevel || '—'}</Typography>
                          </Grid>
                          {inc.rootCause && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" sx={{ color: '#aaa' }}>Root Cause</Typography>
                              <Typography variant="body2" sx={{ color: '#fff' }}>{inc.rootCause}</Typography>
                            </Grid>
                          )}
                          {inc.resolutionSummary && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" sx={{ color: '#aaa' }}>Resolution</Typography>
                              <Typography variant="body2" sx={{ color: '#fff' }}>{inc.resolutionSummary}</Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ─── Create / Edit / View Dialog ──────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {dialogMode === 'create' ? 'New Incident Report' : dialogMode === 'edit' ? `Edit Incident — ${form.incidentId}` : `Incident — ${form.incidentId}`}
          <IconButton size="small" onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <Divider />
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={formTab} onChange={(_, v) => setFormTab(v)} variant="scrollable" scrollButtons="auto">
            {formSections.map((s, i) => <Tab key={i} label={s.label} />)}
          </Tabs>
        </Box>
        <DialogContent sx={{ pt: 2 }}>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          {formSections[formTab].render()}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          {dialogMode === 'view' ? (
            <>
              <Button onClick={() => { setDialogMode('edit'); }}>Edit</Button>
              <Button onClick={() => setDialogOpen(false)}>Close</Button>
            </>
          ) : (
            <>
              <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? <CircularProgress size={20} /> : (dialogMode === 'create' ? 'Create Incident' : 'Save Changes')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* ─── Delete Confirmation ─────────────────────────────────────── */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Incident</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteTarget?.incidentId}</strong> — "{deleteTarget?.title}"?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IncidentReporting;
